import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import createDOMPurify from "@/vendor/purify.es.mjs";
import { marked } from "@/vendor/marked.esm.js";
import { Card, Button, PageHeader, Badge } from "@/components/ui";
import { Icon } from "@/components/IconSprite";
import { API_BASE_URL } from "@/utils/apiClient";
import {
  useCompChecks,
  useCompFindings,
  useCompRuns,
  useStartCompRun,
  useExplainFeedback,
  useExplainFinding,
  type CompFinding,
  type CompMember,
  type CompRun,
  type CompSeverity,
  type ExplainResult,
} from "@/hooks/useCompHealth";

// Base URL of the MLM backoffice this environment pairs with. Findings can
// deep-link straight to the member's profile there; the link works when the
// admin is already signed in to the backoffice as superadmin (it does not
// carry credentials of its own).
const BACKOFFICE_URL = (import.meta.env.VITE_BACKOFFICE_URL || "").replace(/\/+$/, "");

const backofficeProfileUrl = (userId: number | string): string | null =>
  BACKOFFICE_URL ? `${BACKOFFICE_URL}/admin/members/profile/${userId}` : null;

const MemberCell = ({ id, member }: { id: string | null; member: CompMember | undefined }) => {
  if (!id) return <>—</>;
  if (!member) return <>{id}</>;
  return (
    <div style={{ lineHeight: 1.3 }}>
      <div style={{ fontWeight: 600 }}>{member.name || member.username || id}</div>
      <div className="muted" style={{ fontSize: "0.85em" }}>
        {member.member_code || `#${id}`}
        {member.country ? ` · ${member.country}` : ""}
      </div>
    </div>
  );
};

// Compensation-plan health analyzer (Phase A: config drift + invariants).
// Answers Francis's 2026-05-30 inquiry on automated validation of comp-plan
// computations. Read-only against MLM data; verdicts are deterministic SQL —
// no AI participates in deciding whether a payout is correct.

const SEVERITY_BADGE: Record<CompSeverity, "error" | "warning" | "info"> = {
  violation: "error",
  review: "warning",
  info: "info",
};

const STATUS_BADGE: Record<CompRun["status"], "error" | "warning" | "success" | "default" | "info"> = {
  violations: "error",
  review: "warning",
  clean: "success",
  running: "default",
  failed: "error",
};

const formatWhen = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : format(d, "PPp");
};

const dompurify = typeof window === "undefined" ? null : createDOMPurify(window);

const renderMarkdown = (value: string): string => {
  if (!value || !dompurify) return value || "";
  const raw = marked.parse(value, { mangle: false, headerIds: false });
  return dompurify.sanitize(typeof raw === "string" ? raw : String(raw), {
    ALLOWED_TAGS: ["p", "strong", "em", "ul", "ol", "li", "br", "h1", "h2", "h3", "h4", "code", "blockquote"],
    ALLOWED_ATTR: [],
  });
};

// L4 of the analyzer: the AI narrates a finding for a non-technical reader.
// The verdict stays with the deterministic SQL check — the reviewer rating
// below steers only the STYLE of future explanations of the same check.
const ExplainSection = ({ finding, autoStart }: { finding: CompFinding; autoStart: boolean }) => {
  const explain = useExplainFinding();
  const feedback = useExplainFeedback();
  const [result, setResult] = useState<ExplainResult | null>(null);
  const [rated, setRated] = useState<"up" | "down" | null>(null);
  const [comment, setComment] = useState("");
  const [commentSent, setCommentSent] = useState(false);

  useEffect(() => {
    // Reset per finding; optionally auto-generate when opened via the
    // row button so the founder sees the narrative without a second click.
    setResult(null);
    setRated(null);
    setComment("");
    setCommentSent(false);
    if (autoStart) {
      explain.mutate({ findingId: finding.id }, { onSuccess: setResult });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finding.id]);

  const run = (force = false) =>
    explain.mutate({ findingId: finding.id, force }, { onSuccess: setResult });

  const sendRating = (rating: "up" | "down") => {
    setRated(rating);
    feedback.mutate({ findingId: finding.id, rating });
  };

  const sendComment = () => {
    if (!rated || !comment.trim()) return;
    feedback.mutate(
      { findingId: finding.id, rating: rated, comment: comment.trim() },
      { onSuccess: () => setCommentSent(true) }
    );
  };

  return (
    <Card>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <h4 style={{ margin: 0 }}>AI explanation</h4>
        {result ? (
          <Button variant="ghost" onClick={() => run(true)} disabled={explain.isPending}>
            <Icon name="refresh" />
            Regenerate
          </Button>
        ) : (
          <Button onClick={() => run(false)} disabled={explain.isPending}>
            <Icon name="zap" />
            {explain.isPending ? "Thinking…" : "Explain this with AI"}
          </Button>
        )}
      </div>

      {explain.isPending && (
        <p className="muted" style={{ marginBottom: 0 }}>
          Reading the evidence and writing a plain-language explanation…
        </p>
      )}

      {explain.isError && (
        <p className="muted" style={{ marginBottom: 0 }}>
          {explain.error instanceof Error ? explain.error.message : "Explanation failed."}
        </p>
      )}

      {result && !explain.isPending && (
        <>
          <div
            className="rich"
            style={{ marginTop: 10 }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(result.explanation) }}
          />
          <p className="muted" style={{ fontSize: "0.8em", margin: "8px 0 0" }}>
            AI narration of a deterministic database check — it describes and
            suggests verification steps; it does not decide whether the payout
            is correct.
          </p>

          <div className="row" style={{ alignItems: "center", gap: 8, marginTop: 10 }}>
            <span className="muted" style={{ fontSize: "0.85em" }}>Was this explanation helpful?</span>
            <Button
              variant={rated === "up" ? "primary" : "secondary"}
              onClick={() => sendRating("up")}
              disabled={rated !== null}
              aria-label="Helpful"
            >
              <Icon name="check" /> Helpful
            </Button>
            <Button
              variant={rated === "down" ? "primary" : "secondary"}
              onClick={() => sendRating("down")}
              disabled={rated !== null}
              aria-label="Not helpful"
            >
              <Icon name="x" /> Not helpful
            </Button>
            {(result.feedback.up > 0 || result.feedback.down > 0) && (
              <span className="muted" style={{ fontSize: "0.8em" }}>
                {result.feedback.up} helpful · {result.feedback.down} not helpful so far
              </span>
            )}
          </div>

          {rated && !commentSent && (
            <div className="row" style={{ gap: 8, marginTop: 8 }}>
              <input
                className="fi"
                style={{ flex: 1 }}
                placeholder="Optional: tell the AI how to explain this better next time"
                value={comment}
                maxLength={500}
                onChange={(e) => setComment(e.target.value)}
              />
              <Button variant="secondary" onClick={sendComment} disabled={!comment.trim() || feedback.isPending}>
                <Icon name="send" /> Send
              </Button>
            </div>
          )}
          {rated && commentSent && (
            <p className="muted" style={{ fontSize: "0.85em", margin: "8px 0 0" }}>
              Thank you — future explanations of this check will take your feedback into account.
            </p>
          )}
        </>
      )}
    </Card>
  );
};

interface FindingDrawerProps {
  finding: CompFinding | null;
  members: Record<string, CompMember>;
  autoExplain: boolean;
  onClose: () => void;
}

const FindingDrawer = ({ finding, members, autoExplain, onClose }: FindingDrawerProps) => {
  if (!finding) return null;
  const detail = finding.detail ?? {};
  const member = finding.member_id ? members[finding.member_id] : undefined;
  const profileUrl = member ? backofficeProfileUrl(member.user_id) : null;
  return (
    <>
      <div className="dw-ov open" onClick={onClose} />
      <aside className="dw open">
        <div className="dw-h">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3>
              Finding #{finding.id}{" "}
              <Badge variant={SEVERITY_BADGE[finding.severity]}>{finding.severity}</Badge>
            </h3>
            <p>{finding.title ?? finding.check_id}</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close drawer">
            <Icon name="x" />
          </button>
        </div>
        <div className="dw-b">
          <Card>
            <h4 style={{ marginTop: 0 }}>Summary</h4>
            <table className="tbl">
              <tbody>
                <tr><td style={{ fontWeight: 600, width: "40%" }}>Check</td><td>{finding.check_id}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Member</td><td><MemberCell id={finding.member_id} member={member} /></td></tr>
                <tr><td style={{ fontWeight: 600 }}>Bonus type</td><td>{finding.payment_type ?? "—"}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Amount</td><td>{finding.amount ?? "—"}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Date</td><td>{finding.occurred_on ?? "—"}</td></tr>
              </tbody>
            </table>
          </Card>
          {member && (
            <Card>
              <div className="row" style={{ justifyContent: "space-between", alignItems: "start" }}>
                <h4 style={{ marginTop: 0 }}>Member</h4>
                {profileUrl && (
                  <a href={profileUrl} target="_blank" rel="noreferrer">
                    <Button variant="secondary">
                      <Icon name="user" />
                      View in Backoffice
                    </Button>
                  </a>
                )}
              </div>
              <table className="tbl">
                <tbody>
                  <tr><td style={{ fontWeight: 600, width: "40%" }}>Name</td><td>{member.name || "—"}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Username</td><td>{member.username || "—"}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Member ID</td><td>{member.member_code || `#${member.user_id}`}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Country</td><td>{member.country || "—"}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Rank</td><td>{member.rank_name || (member.rank_id != null ? `Rank ${member.rank_id}` : "—")}</td></tr>
                  <tr><td style={{ fontWeight: 600 }}>Status</td><td>{member.active == null ? "—" : member.active ? "Active" : "Inactive"}</td></tr>
                </tbody>
              </table>
              <p className="muted" style={{ marginBottom: 0, fontSize: "0.85em" }}>
                Opens the member's profile in the MLM backoffice. Requires an
                active superadmin session there.
              </p>
            </Card>
          )}
          <ExplainSection finding={finding} autoStart={autoExplain} />
          <Card>
            <h4 style={{ marginTop: 0 }}>Evidence</h4>
            <p className="muted" style={{ marginTop: 0 }}>
              Raw row(s) returned by the invariant query — including the exact
              commission ids involved, so the finding can be verified directly
              in the database.
            </p>
            <pre style={{ overflowX: "auto", whiteSpace: "pre-wrap", margin: 0 }}>
              {JSON.stringify(detail, null, 2)}
            </pre>
          </Card>
        </div>
      </aside>
    </>
  );
};

const CompHealth = () => {
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [checkFilter, setCheckFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<CompSeverity | "all">("all");
  const [selectedFinding, setSelectedFinding] = useState<CompFinding | null>(null);
  const [autoExplain, setAutoExplain] = useState(false);
  const [windowDays, setWindowDays] = useState(90);

  const { data: checksData } = useCompChecks();
  const { data: runsData, isLoading: runsLoading, refetch: refetchRuns, isFetching } = useCompRuns();
  const startRun = useStartCompRun();

  const runs = runsData?.runs ?? [];
  const activeRun = useMemo(() => {
    if (selectedRunId !== null) return runs.find((r) => r.id === selectedRunId) ?? null;
    return runs[0] ?? null;
  }, [runs, selectedRunId]);

  const { data: findingsData, isLoading: findingsLoading } = useCompFindings(
    activeRun?.id ?? null,
    {
      check_id: checkFilter !== "all" ? checkFilter : undefined,
      severity: severityFilter !== "all" ? severityFilter : undefined,
      limit: 200,
    }
  );
  const findings = findingsData?.findings ?? [];
  const membersMap = findingsData?.members ?? {};
  const summaryChecks = activeRun?.summary?.checks ?? {};

  return (
    <>
      <PageHeader
        label="Administration"
        title="Comp Plan Health"
        description="Automated validation of compensation computations — config drift plus per-way-to-earn invariants. Read-only; verdicts are deterministic SQL."
        actions={
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <label className="fl" htmlFor="ch-window" style={{ marginBottom: 0 }}>Window</label>
            <select
              id="ch-window"
              className="fi"
              style={{ width: 130 }}
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
            >
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
              <option value={730}>2 years</option>
            </select>
            <Button
              onClick={() => startRun.mutate({ window_days: windowDays })}
              disabled={startRun.isPending}
            >
              <Icon name="play" />
              {startRun.isPending ? "Running…" : "Run health check"}
            </Button>
            <Button variant="secondary" onClick={() => refetchRuns()} disabled={isFetching} aria-label="Refresh runs">
              <Icon name="refresh" />
              {isFetching ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        }
      />

      {startRun.isError && (
        <Card>
          <p className="muted" style={{ margin: 0 }}>
            {startRun.error instanceof Error
              ? startRun.error.message
              : "Health run failed."}
          </p>
        </Card>
      )}

      {/* Traffic-light summary for the active run */}
      {activeRun && (
        <Card>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h4 style={{ margin: 0 }}>
                Run #{activeRun.id}{" "}
                <Badge variant={STATUS_BADGE[activeRun.status]}>{activeRun.status}</Badge>
              </h4>
              <p className="muted" style={{ margin: "4px 0 0" }}>
                {formatWhen(activeRun.started_at)} · window {activeRun.window_days} days · by{" "}
                {activeRun.triggered_by ?? "—"}
                {activeRun.summary?.window_start ? ` · from ${activeRun.summary.window_start}` : ""}
              </p>
            </div>
            <a
              href={`${API_BASE_URL}/api/admin/comp-health/runs/${activeRun.id}/export`}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="secondary">
                <Icon name="file" />
                Export CSV
              </Button>
            </a>
          </div>

          {activeRun.error && (
            <p className="muted" style={{ marginBottom: 0 }}>Error: {activeRun.error}</p>
          )}

          <table className="tbl" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Check</th>
                <th>Severity</th>
                <th style={{ textAlign: "right" }}>Hits</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(summaryChecks).map(([checkId, info]) => (
                <tr
                  key={checkId}
                  onClick={() => setCheckFilter(checkId)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{info.title}</td>
                  <td>
                    <Badge variant={info.count > 0 ? SEVERITY_BADGE[info.severity] : "success"}>
                      {info.count > 0 ? info.severity : "clean"}
                    </Badge>
                  </td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {info.count.toLocaleString()}
                  </td>
                </tr>
              ))}
              {(activeRun.summary?.config_drift_tables ?? 0) > 0 && (
                <tr onClick={() => setCheckFilter("config_drift")} style={{ cursor: "pointer" }}>
                  <td>Settings changed since previous run</td>
                  <td><Badge variant="warning">review</Badge></td>
                  <td style={{ textAlign: "right" }}>{activeRun.summary?.config_drift_tables}</td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="muted" style={{ marginBottom: 0 }}>
            Hit counts are totals; the findings below are a bounded evidence
            sample (up to {activeRun.summary?.sample_limit ?? 200} per check).
          </p>
        </Card>
      )}

      {/* Findings */}
      <Card>
        <div className="row c3" style={{ alignItems: "end" }}>
          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="ch-run">Run</label>
            <select
              id="ch-run"
              className="fi"
              value={activeRun?.id ?? ""}
              onChange={(e) => setSelectedRunId(Number(e.target.value))}
            >
              {runs.map((r) => (
                <option key={r.id} value={r.id}>
                  #{r.id} · {formatWhen(r.started_at)} · {r.status}
                </option>
              ))}
            </select>
          </div>
          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="ch-check">Check</label>
            <select
              id="ch-check"
              className="fi"
              value={checkFilter}
              onChange={(e) => setCheckFilter(e.target.value)}
            >
              <option value="all">All checks</option>
              {(checksData?.checks ?? []).map((c) => (
                <option key={c.check_id} value={c.check_id}>{c.title}</option>
              ))}
              <option value="config_drift">Settings drift</option>
              <option value="config_baseline">Settings baseline</option>
            </select>
          </div>
          <div className="fg" style={{ marginBottom: 0 }}>
            <label className="fl" htmlFor="ch-sev">Severity</label>
            <select
              id="ch-sev"
              className="fi"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value as CompSeverity | "all")}
            >
              <option value="all">All severities</option>
              <option value="violation">Violation</option>
              <option value="review">Review</option>
              <option value="info">Info</option>
            </select>
          </div>
        </div>
      </Card>

      <Card>
        {runsLoading || findingsLoading ? (
          <p className="muted">Loading…</p>
        ) : !activeRun ? (
          <p className="muted">
            No health runs yet. Choose a window and press “Run health check” —
            the run snapshots every settings table and executes all invariants
            read-only.
          </p>
        ) : findings.length === 0 ? (
          <p className="muted">No findings match these filters.</p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Check</th>
                <th>Member</th>
                <th>Bonus type</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th>Date</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {findings.map((f) => (
                <tr
                  key={f.id}
                  onClick={() => { setAutoExplain(false); setSelectedFinding(f); }}
                  style={{ cursor: "pointer" }}
                >
                  <td><Badge variant={SEVERITY_BADGE[f.severity]}>{f.severity}</Badge></td>
                  <td>{f.title ?? f.check_id}</td>
                  <td><MemberCell id={f.member_id} member={f.member_id ? membersMap[f.member_id] : undefined} /></td>
                  <td>{f.payment_type ?? "—"}</td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {f.amount ?? "—"}
                  </td>
                  <td>{f.occurred_on ?? "—"}</td>
                  <td style={{ textAlign: "right" }}>
                    <Button
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAutoExplain(true);
                        setSelectedFinding(f);
                      }}
                    >
                      <Icon name="zap" />
                      Explain with AI
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <FindingDrawer
        finding={selectedFinding}
        members={membersMap}
        autoExplain={autoExplain}
        onClose={() => setSelectedFinding(null)}
      />
    </>
  );
};

export default CompHealth;
